import { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  FiChevronDown,
  FiDownload,
  FiHash,
  FiPercent,
  FiRefreshCw,
  FiSearch,
} from 'react-icons/fi';
import Text from 'src/design/styleguide/text/Text';
import history from 'src/browser/history';
import PropertyService from 'scripts/services/PropertyService';
import PortfolioOverTimeChart from 'src/components/portfolioWidget/portfolioOverTimeChart/PortfolioOverTimeChart';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import { breakpointSmall } from 'src/design/styleguide/common/breakpoints';
import { propertyUrl } from 'src/utils/pageUrls';
import { greenDark } from 'src/themes/brickx/colors';
import {
  getFilteredPortfolioHoldings,
  getPortfolioHoldingsForPlan,
  getPortfolioInvestmentType,
  getPortfolioOwnership,
  getPortfolioComposition,
} from '../advisorClientDetailData';
import AdvisorTableEmptyState from '../AdvisorTableEmptyState';
import { useClientDetail } from './ClientDetailContext';
import {
  Card,
  CardHeader,
  CardTitle,
  DataFooter,
  DataTable,
  DataTableScroll,
  DataToolbar,
  DetailLabel,
  DetailValue,
  FilterSelect,
  OrdersExport,
  OrdersLayout,
  OrdersSelectField,
  PageInfo,
  SearchField,
  TransactionAmount,
} from './styles';
import {
  FILTER_PLAN_TYPES,
  downloadCsv,
  formatCount,
  formatCurrency,
  formatPercentage,
  formatPlanType,
  getValueTone,
} from './helpers';
import { OrdersPropertyCode, PaginationControls } from './components';
import { useActivityLayout, usePortfolioHistory } from './hooks';

const HOLDINGS_PAGE_SIZE = 10;

const HOLDING_INVESTMENT_TYPES = [
  ['property', 'Property'],
  ['mortgage', 'Mortgage'],
  ['financial_asset', 'Financial assets'],
  ['development', 'Development'],
];

const formatHoldingOwnership = (value) =>
  Number.isFinite(value)
    ? new Intl.NumberFormat('en-AU', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(value)
    : '\u2014';

const HOLDING_COLUMNS = [
  { key: 'propertyCode', label: 'Investment' },
  { key: 'bricks', label: 'Bricks' },
  {
    key: 'averageCostPerBrick',
    label: 'Cost / Brick',
    fullLabel: 'Avg. Cost / Brick',
  },
  { key: 'brickValue', label: 'Value / Brick', fullLabel: 'Brick Value' },
  { key: 'investment', label: 'Invested', fullLabel: 'Amount Invested' },
  { key: 'portfolioValue', label: 'Value', fullLabel: 'Portfolio Value' },
  { key: 'roi', label: 'ROI' },
  { key: 'distributions', label: 'Dist.', fullLabel: 'Distributions' },
];

const COMPOSITION_COLORS = [
  'var(--bx-color---purple-500)',
  'var(--bx-color---yellow-500)',
  greenDark,
  'var(--bx-color---blue-500)',
  'var(--bx-color---grey-400)',
];

const getCompositionGradient = (composition) => {
  if (!composition.length) {
    return 'conic-gradient(var(--bx-color---grey-200) 0 100%)';
  }

  let start = 0;
  const segments = composition.map((item, index) => {
    const end = start + item.percentage * 100;
    const segment = `${COMPOSITION_COLORS[index]} ${start}% ${end}%`;
    start = end;
    return segment;
  });

  return `conic-gradient(${segments.join(', ')})`;
};

const PortfolioLayout = styled.div`
  display: grid;
  min-width: 0;
  gap: ${gridUnit * 4}px;

  ${CardTitle} {
    font-size: 20px;
  }
`;

const PortfolioSummaryMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 0;
  row-gap: ${gridUnit * 4}px;
  padding: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;

  ${breakpointSmall(`
    grid-template-columns: repeat(3, minmax(0, 1fr));
  `)}

  @media (min-width: 1280px) {
    grid-template-columns: repeat(6, minmax(max-content, 1fr));
  }
`;

const PortfolioHistoryBody = styled.div`
  min-width: 0;
  padding: 0 var(--advisor-card-gutter) ${gridUnit * 6}px;
`;

const PortfolioMetric = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  min-width: 0;
  padding: 0 ${gridUnit * 6}px;
  border-left: 1px solid var(--bx-color---grey-200);

  &:nth-child(2n + 1) {
    border-left: 0;
    padding-left: 0;
  }

  &:nth-child(2n) {
    padding-right: 0;
  }

  ${DetailLabel} {
    color: var(--bx-color---grey-700);
    font-size: 16px;
  }

  ${DetailValue} {
    font-size: 18px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  ${breakpointSmall(`
    &:nth-child(n) {
      border-left: 1px solid var(--bx-color---grey-200);
      padding: 0 ${gridUnit * 6}px;
    }

    &:nth-child(3n + 1) {
      border-left: 0;
      padding-left: 0;
    }

    &:nth-child(3n) {
      padding-right: 0;
    }
  `)}

  @media (min-width: 1280px) {
    &:nth-child(n) {
      border-left: 1px solid var(--bx-color---grey-200);
      padding: 0 ${gridUnit * 6}px;
    }

    &:first-child {
      border-left: 0;
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

const PortfolioVisuals = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);

  &::before {
    position: absolute;
    top: 0;
    right: var(--advisor-card-gutter);
    left: var(--advisor-card-gutter);
    height: 1px;
    background: var(--bx-color---grey-200);
    content: '';
  }
`;

const CompositionPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 396px);
  justify-content: center;
  gap: ${gridUnit * 8}px;
  align-items: center;
  padding: ${gridUnit * 6}px var(--advisor-card-gutter);

  &[data-chart-layout='inline'] {
    grid-template-columns: 240px minmax(0, 396px);
  }
`;

const Donut = styled.div`
  display: flex;
  width: 240px;
  height: auto;
  max-width: 100%;
  aspect-ratio: 1;
  justify-self: center;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${(props) => props.$background};
`;

const DonutCenter = styled.div`
  display: flex;
  width: 58%;
  height: 58%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--bx-color---surface);
`;

const DonutValue = styled(Text).attrs({
  as: 'span',
  fontSize: '2x-small',
  fontWeight: 'bold',
})`
  color: var(--bx-color---grey-800);
  font-size: 1.8rem;
  text-align: center;
`;

const DonutLabel = styled(Text).attrs({ as: 'span', fontSize: '5x-small' })`
  margin-top: ${gridUnit}px;
  color: var(--bx-color---grey-700);
  font-size: 16px;
`;

const CompositionLegend = styled.div`
  display: grid;
  gap: ${gridUnit * 4}px;
`;

const CompositionLegendRow = styled.div`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 60px;
  column-gap: ${gridUnit * 3}px;
  row-gap: ${gridUnit}px;
  align-items: center;
  color: var(--bx-color---grey-700);
  font-size: 16px;

  ${CompositionLegend}[data-legend-layout='compact'] & {
    grid-template-columns: 10px minmax(0, 1fr) 60px;
  }
`;

const LegendDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(props) => props.$color};
`;

const LegendName = styled.span`
  display: flex;
  gap: ${gridUnit}px;
  min-width: 0;
  white-space: nowrap;

  ${CompositionLegend}[data-legend-layout='compact'] & {
    grid-column: 2 / -1;
  }
`;

const LegendDescription = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegendCode = styled.span`
  flex-shrink: 0;
`;

const LegendValue = styled.span`
  color: var(--bx-color---grey-800);
  font-weight: 500;
  white-space: nowrap;

  ${CompositionLegend}[data-legend-layout='compact'] & {
    grid-column: 2;
  }
`;

const LegendPercentage = styled.span`
  color: var(--bx-color---grey-700);
  text-align: right;
`;

const PortfolioHoldingsLayout = styled(OrdersLayout)`
  ${CardHeader} {
    flex-wrap: wrap;
  }

  ${CardHeader} button,
  ${DataFooter} button {
    font-size: 16px;
  }

  ${DataTable} {
    min-width: 760px;

    th,
    td {
      padding-right: ${gridUnit * 2}px;
      padding-left: ${gridUnit * 2}px;
    }

    th {
      text-align: left;
      white-space: nowrap;
    }

    th:last-child,
    td:last-child {
      width: auto;
    }

    th:nth-child(2),
    td:nth-child(2) {
      min-width: 96px;
      width: 96px;
    }

    td:nth-child(2),
    td:nth-child(5),
    td:nth-child(6),
    td:nth-child(7) {
      font-weight: 700;
    }

    td:nth-child(5) {
      color: var(--bx-color---purple-600);
    }

    td:nth-child(6) {
      color: var(--bx-color---yellow-600);
    }
  }

  &[data-filter-layout='split'] ${DataToolbar} > :last-child {
    grid-column: auto;
    grid-row: auto;
  }
`;

const HoldingHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${gridUnit * 2}px;
`;

const HoldingQuantityToggle = styled.div`
  display: inline-flex;
  border: 1px solid var(--bx-color---grey-300);
  border-radius: 4px;

  button {
    display: inline-flex;
    width: 40px;
    height: 38px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: var(--bx-color---surface);
    color: var(--bx-color---grey-700);
    cursor: pointer;
  }

  button:first-child {
    border-right: 1px solid var(--bx-color---grey-300);
    border-radius: 3px 0 0 3px;
  }

  button:last-child {
    border-radius: 0 3px 3px 0;
  }

  button[aria-pressed='true'] {
    background: var(--bx-color---purple-50);
    color: var(--bx-color---purple-600);
  }

  button:disabled {
    opacity: 0.5;
    cursor: wait;
  }

  button:focus-visible {
    outline: 2px solid var(--bx-color---purple-600);
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const HoldingSortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${gridUnit}px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;

  span {
    min-width: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const PortfolioComposition = ({ composition, portfolioValue }) => {
  const panelRef = useRef(null);
  const legendRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(0);
  const [legendCompact, setLegendCompact] = useState(false);

  useEffect(() => {
    const panel = panelRef.current;
    const updateLayout = () => {
      setPanelWidth(panel.clientWidth);
      const legend = legendRef.current;
      setLegendCompact(
        Array.from(legend.children).some((row) => {
          const name = row.children[1];
          const nameWidth = Array.from(name.children).reduce(
            (width, element) => width + element.scrollWidth,
            (name.children.length - 1) * gridUnit,
          );
          const amountWidth =
            row.children[2].firstElementChild.getBoundingClientRect().width;
          return (
            10 + nameWidth + amountWidth + 60 + gridUnit * 9 >
            legend.clientWidth
          );
        }),
      );
    };
    updateLayout();
    if (typeof window.ResizeObserver === 'function') {
      const observer = new window.ResizeObserver(updateLayout);
      observer.observe(panel);
      observer.observe(legendRef.current);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [composition]);

  return (
    <CompositionPanel
      ref={panelRef}
      data-chart-layout={panelWidth >= 760 ? 'inline' : 'stacked'}
    >
      <Donut
        $background={getCompositionGradient(composition)}
        role="img"
        aria-label={`Portfolio value ${formatCurrency(portfolioValue)} by investment`}
      >
        <DonutCenter>
          <DonutValue>{formatCurrency(portfolioValue)}</DonutValue>
          <DonutLabel>Total Value</DonutLabel>
        </DonutCenter>
      </Donut>
      <CompositionLegend
        ref={legendRef}
        data-legend-layout={legendCompact ? 'compact' : 'inline'}
      >
        {composition.map((item, index) => (
          <CompositionLegendRow key={item.key}>
            <LegendDot $color={COMPOSITION_COLORS[index]} />
            <LegendName
              title={
                item.propertyCode
                  ? `${item.label} (${item.propertyCode})`
                  : item.label
              }
            >
              <LegendDescription>{item.label}</LegendDescription>
              {item.propertyCode && (
                <LegendCode>({item.propertyCode})</LegendCode>
              )}
            </LegendName>
            <LegendValue>
              <span>{formatCurrency(item.value)}</span>
            </LegendValue>
            <LegendPercentage>
              {(item.percentage * 100).toFixed(1)}%
            </LegendPercentage>
          </CompositionLegendRow>
        ))}
      </CompositionLegend>
    </CompositionPanel>
  );
};

PortfolioComposition.propTypes = {
  composition: PropTypes.arrayOf(PropTypes.object).isRequired,
  portfolioValue: PropTypes.number,
};

const PortfolioSummaryCard = ({ detail }) => {
  const { financials, holdings } = detail;
  const composition = getPortfolioComposition(holdings);
  const metrics = [
    ['Portfolio Value', formatCurrency(financials.portfolioValue)],
    ['Cash Invested', formatCurrency(financials.amountInvested)],
    [
      'Total ROI',
      formatPercentage(financials.totalRoi),
      getValueTone(financials.totalRoi),
    ],
    ['Distributions', formatCurrency(financials.distributions)],
    ['Investments', formatCount(financials.properties)],
    ['Bricks', formatCount(financials.bricks)],
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Portfolio Summary</CardTitle>
      </CardHeader>
      <PortfolioSummaryMetrics>
        {metrics.map(([label, value, tone]) => (
          <PortfolioMetric key={label}>
            <DetailLabel>{label}</DetailLabel>
            <DetailValue className={tone}>{value}</DetailValue>
          </PortfolioMetric>
        ))}
      </PortfolioSummaryMetrics>
      {!!composition.length && (
        <PortfolioVisuals>
          <PortfolioComposition
            composition={composition}
            portfolioValue={financials.portfolioValue}
          />
        </PortfolioVisuals>
      )}
    </Card>
  );
};

PortfolioSummaryCard.propTypes = {
  detail: PropTypes.object.isRequired,
};

const HoldingsCard = ({ holdings, plans, properties, propertiesLoading }) => {
  const { containerRef, filterLayout } = useActivityLayout();
  const [search, setSearch] = useState('');
  const [state, setState] = useState('all');
  const [planType, setPlanType] = useState('all');
  const [investmentType, setInvestmentType] = useState('all');
  const [quantityMode, setQuantityMode] = useState('bricks');
  const [issuedFinancials, setIssuedFinancials] = useState(null);
  const [sortBy, setSortBy] = useState('propertyCode');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  useEffect(() => {
    let cancelled = false;
    PropertyService.getAllFinancials()
      .then((financials) => {
        if (!cancelled) setIssuedFinancials(financials || []);
      })
      .catch(() => {
        if (!cancelled) setIssuedFinancials([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const states = Array.from(
    new Set(holdings.map((holding) => holding.state).filter(Boolean)),
  ).sort();
  const holdingColumns = HOLDING_COLUMNS.map((column) =>
    column.key === 'bricks' && quantityMode === 'ownership'
      ? { key: 'ownership', label: 'Ownership', fullLabel: 'Ownership (%)' }
      : column,
  );
  const selectedHoldings = getPortfolioHoldingsForPlan(
    holdings,
    plans,
    planType,
  ).map((holding) => {
    const property = (properties || []).find(
      (item) => item.propertyCode === holding.propertyCode,
    );
    const financials = (issuedFinancials || []).find(
      (item) => item.propertyCode === holding.propertyCode,
    );
    return {
      ...holding,
      investmentType: getPortfolioInvestmentType(property),
      ownership: getPortfolioOwnership(
        holding.bricks,
        financials && financials.numberBricks,
      ),
    };
  });
  const filteredHoldings = getFilteredPortfolioHoldings(selectedHoldings, {
    search,
    state,
    investmentType,
    sortBy,
    sortDirection,
  });
  const totalPages = Math.max(
    1,
    Math.ceil(filteredHoldings.length / HOLDINGS_PAGE_SIZE),
  );
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * HOLDINGS_PAGE_SIZE;
  const visibleHoldings = filteredHoldings.slice(
    firstIndex,
    firstIndex + HOLDINGS_PAGE_SIZE,
  );

  useEffect(
    () => setPage(1),
    [search, state, planType, investmentType, sortBy, sortDirection],
  );

  const changeQuantityMode = (mode) => {
    setQuantityMode(mode);
    if (sortBy === 'bricks' || sortBy === 'ownership') {
      setSortBy(mode === 'bricks' ? 'bricks' : 'ownership');
    }
  };

  const exportHoldings = () => {
    const columns = [
      ...holdingColumns,
      { key: 'location', label: 'Location' },
      { key: 'state', label: 'State' },
    ];
    const rows = [
      columns.map((column) => column.fullLabel || column.label),
      ...filteredHoldings.map((holding) =>
        columns
          .map((column) =>
            column.key === 'ownership' && holding.ownership !== null
              ? Number((holding.ownership * 100).toFixed(4))
              : holding[column.key],
          )
          .map((value) =>
            typeof value === 'string' && /^[=+\-@\t\r\n]/.test(value)
              ? `'${value}`
              : value,
          ),
      ),
    ];
    downloadCsv('client-holdings.csv', rows);
  };

  const changeColumnSort = (key) => {
    setSortBy(key);
    setSortDirection(
      sortBy === key && sortDirection === 'asc' ? 'desc' : 'asc',
    );
  };

  return (
    <PortfolioHoldingsLayout
      ref={containerRef}
      data-filter-layout={filterLayout}
    >
      <Card>
        <CardHeader>
          <CardTitle>Holdings</CardTitle>
          <HoldingHeaderActions>
            <HoldingQuantityToggle
              role="group"
              aria-label="Holdings quantity display"
            >
              <button
                type="button"
                aria-label="Show Bricks"
                title="Show Bricks"
                aria-pressed={quantityMode === 'bricks'}
                onClick={() => changeQuantityMode('bricks')}
              >
                <FiHash aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Show ownership percentage"
                title="Show ownership percentage"
                disabled={issuedFinancials === null}
                aria-pressed={quantityMode === 'ownership'}
                onClick={() => changeQuantityMode('ownership')}
              >
                <FiPercent aria-hidden="true" />
              </button>
            </HoldingQuantityToggle>
            <OrdersExport
              type="button"
              disabled={!filteredHoldings.length}
              onClick={exportHoldings}
            >
              <FiDownload aria-hidden="true" />
              Export
            </OrdersExport>
          </HoldingHeaderActions>
        </CardHeader>
        <DataToolbar>
          <SearchField>
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={search}
              placeholder="Search investments..."
              aria-label="Search client investments"
              onChange={(event) => setSearch(event.target.value)}
            />
          </SearchField>
          <OrdersSelectField>
            <FilterSelect
              value={planType}
              aria-label="Filter holdings by plan"
              onChange={(event) => setPlanType(event.target.value)}
            >
              <option value="all">All plans</option>
              {FILTER_PLAN_TYPES.map((type) => (
                <option key={type} value={type}>
                  {formatPlanType(type)}
                </option>
              ))}
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersSelectField>
          <OrdersSelectField>
            <FilterSelect
              value={investmentType}
              aria-label="Filter holdings by investment type"
              disabled={propertiesLoading}
              onChange={(event) => setInvestmentType(event.target.value)}
            >
              <option value="all">All types</option>
              {HOLDING_INVESTMENT_TYPES.map(([type, label]) => (
                <option key={type} value={type}>
                  {label}
                </option>
              ))}
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersSelectField>
          <OrdersSelectField>
            <FilterSelect
              value={state}
              aria-label="Filter holdings by state"
              onChange={(event) => setState(event.target.value)}
            >
              <option value="all">All states</option>
              {states.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </FilterSelect>
            <FiChevronDown aria-hidden="true" />
          </OrdersSelectField>
        </DataToolbar>
        {visibleHoldings.length ? (
          <Fragment>
            <DataTableScroll>
              <DataTable aria-label="Client holdings">
                <thead>
                  <tr>
                    {holdingColumns.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        aria-sort={
                          sortBy === column.key
                            ? sortDirection === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        <HoldingSortButton
                          type="button"
                          title={`Sort by ${column.fullLabel || column.label}`}
                          onClick={() => changeColumnSort(column.key)}
                        >
                          <span>{column.label}</span>
                          <FiChevronDown
                            aria-hidden="true"
                            style={{
                              visibility:
                                sortBy === column.key ? 'visible' : 'hidden',
                              transform:
                                sortDirection === 'asc'
                                  ? 'rotate(180deg)'
                                  : undefined,
                            }}
                          />
                        </HoldingSortButton>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleHoldings.map((holding, index) => (
                    <tr
                      key={holding.propertyCode || index}
                      tabIndex={0}
                      onClick={() =>
                        history.push(propertyUrl(holding.propertyCode))
                      }
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          history.push(propertyUrl(holding.propertyCode));
                        }
                      }}
                    >
                      <td>
                        <OrdersPropertyCode
                          order={holding}
                          properties={properties}
                          propertiesLoading={propertiesLoading}
                        />
                      </td>
                      <td
                        className="numeric"
                        title={`Bricks: ${formatCount(holding.bricks)}; Ownership: ${formatHoldingOwnership(holding.ownership)}`}
                      >
                        {quantityMode === 'ownership'
                          ? formatHoldingOwnership(holding.ownership)
                          : formatCount(holding.bricks)}
                      </td>
                      <td className="numeric">
                        {formatCurrency(holding.averageCostPerBrick)}
                      </td>
                      <td className="numeric">
                        {formatCurrency(holding.brickValue)}
                      </td>
                      <td className="numeric">
                        {formatCurrency(holding.investment)}
                      </td>
                      <td className="numeric">
                        {formatCurrency(holding.portfolioValue)}
                      </td>
                      <td className="numeric">
                        <TransactionAmount
                          className={getValueTone(holding.roi)}
                        >
                          {formatPercentage(holding.roi)}
                        </TransactionAmount>
                      </td>
                      <td className="numeric">
                        {formatCurrency(holding.distributions)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </DataTableScroll>
            <DataFooter>
              <PageInfo>
                {`Showing ${firstIndex + 1} to ${Math.min(
                  firstIndex + HOLDINGS_PAGE_SIZE,
                  filteredHoldings.length,
                )} of ${filteredHoldings.length} investments`}
              </PageInfo>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                label="Holding pages"
                onChange={setPage}
              />
            </DataFooter>
          </Fragment>
        ) : (
          <DataTableScroll>
            <AdvisorTableEmptyState>
              {holdings.length
                ? 'No investments match the selected filters.'
                : 'No current holdings are available.'}
            </AdvisorTableEmptyState>
          </DataTableScroll>
        )}
      </Card>
    </PortfolioHoldingsLayout>
  );
};

HoldingsCard.propTypes = {
  holdings: PropTypes.arrayOf(PropTypes.object).isRequired,
  plans: PropTypes.arrayOf(PropTypes.object).isRequired,
  properties: PropTypes.arrayOf(PropTypes.object),
  propertiesLoading: PropTypes.bool,
};

const PortfolioHistoryCard = ({ clientId }) => {
  const { loading, data, error, retry } = usePortfolioHistory(clientId);
  const hasHistory = data.some(
    (plan) => plan.accountValueAcrossTime.length > 0,
  );

  return (
    <Card aria-labelledby="client-portfolio-history-title">
      <CardHeader>
        <CardTitle id="client-portfolio-history-title">
          Account Value Over Time
        </CardTitle>
        {!loading && error && (
          <OrdersExport onClick={retry}>
            <FiRefreshCw aria-hidden="true" />
            Retry
          </OrdersExport>
        )}
      </CardHeader>
      <PortfolioHistoryBody aria-busy={loading}>
        {loading ? (
          <AdvisorTableEmptyState>
            Loading portfolio history...
          </AdvisorTableEmptyState>
        ) : error ? (
          <AdvisorTableEmptyState>
            Unable to load portfolio history.
          </AdvisorTableEmptyState>
        ) : !hasHistory ? (
          <AdvisorTableEmptyState style={{ paddingBottom: 0 }}>
            No portfolio history available.
          </AdvisorTableEmptyState>
        ) : (
          <PortfolioOverTimeChart data={data} />
        )}
      </PortfolioHistoryBody>
    </Card>
  );
};

PortfolioHistoryCard.propTypes = {
  clientId: PropTypes.string.isRequired,
};

const PortfolioTab = () => {
  const { clientId, detail, properties, propertiesLoading } = useClientDetail();
  return (
    <PortfolioLayout>
      <PortfolioSummaryCard detail={detail} />
      <HoldingsCard
        holdings={detail.holdings}
        plans={detail.plans}
        properties={properties}
        propertiesLoading={propertiesLoading}
      />
      <PortfolioHistoryCard clientId={clientId} />
    </PortfolioLayout>
  );
};

export default PortfolioTab;
